import { Form } from "@inertiajs/react"
import { type ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
} from "@/components/responsive-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useOnline } from "@/hooks/use-online"
import { adminBlocklistEntriesPath, adminBlocklistEntryPath } from "@/routes"
import type { BlocklistDuplicateFound, BlocklistEntry } from "@/types/admin"

type Mode =
  | { kind: "add"; trigger: ReactNode }
  | { kind: "edit"; entry: BlocklistEntry; trigger: ReactNode }
  | {
      kind: "overwrite"
      duplicate: BlocklistDuplicateFound
      onClose: () => void
    }

interface BlocklistFormDialogProps {
  mode: Mode
}

/**
 * BlocklistFormDialog — three render modes share the same form chrome:
 *
 *   - "add"       → POST /admin/blocklist with { handle, reason }
 *   - "edit"      → PATCH /admin/blocklist/:id with { reason }
 *   - "overwrite" → server-told hot-swap from the duplicate_found prop
 *                   per UX Spec §3 F4. PATCH /admin/blocklist/:id with
 *                   the admin's confirmed new reason.
 *
 * Forms use Inertia `<Form>` with plain shadcn primitives + the render-
 * prop `errors` object (Crew hard rules: no shadcn Form, no react-
 * hook-form, no zod). Cancel is autoFocused per UX Spec §5.
 *
 * Test IDs (locked v6 contract): `admin-blocklist-add-dialog`,
 * `admin-blocklist-edit-dialog`, `admin-blocklist-form-handle`,
 * `admin-blocklist-form-reason`, `admin-blocklist-form-submit`,
 * `admin-blocklist-duplicate-warning` (only on overwrite mode).
 */
export function BlocklistFormDialog(props: BlocklistFormDialogProps) {
  if (props.mode.kind === "overwrite") {
    return <OverwriteDialog mode={props.mode} />
  }
  return <AddOrEditDialog mode={props.mode} />
}

// ---------------------------------------------------------------------------
// Add / Edit — click-to-open from a trigger
// ---------------------------------------------------------------------------

interface AddOrEditDialogProps {
  mode:
    | { kind: "add"; trigger: ReactNode }
    | { kind: "edit"; entry: BlocklistEntry; trigger: ReactNode }
}

function AddOrEditDialog({ mode }: AddOrEditDialogProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [open, setOpen] = useState(false)

  const isEdit = mode.kind === "edit"
  const entry = isEdit ? mode.entry : null

  const action = isEdit
    ? adminBlocklistEntryPath({ id: entry!.id })
    : adminBlocklistEntriesPath()
  const method = isEdit ? "patch" : "post"
  const title = isEdit
    ? t("admin.blocklist.form.title_edit", { handle: `@${entry!.handle}` })
    : t("admin.blocklist.form.title_add")
  const submitLabel = isEdit
    ? t("admin.blocklist.form.edit_submit")
    : t("admin.blocklist.form.submit")

  return (
    <ResponsiveDialog open={open} onOpenChange={setOpen}>
      <ResponsiveDialogTrigger asChild>{mode.trigger}</ResponsiveDialogTrigger>
      <ResponsiveDialogContent
        className="sm:max-w-md"
        data-testid={
          isEdit ? "admin-blocklist-edit-dialog" : "admin-blocklist-add-dialog"
        }
      >
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{title}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription className="sr-only">
            {title}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        {/* OKS-120 — Form acts as scrollable body in mobile Sheet mode. */}
        <Form
          action={action}
          method={method}
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
          resetOnSuccess
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-0"
        >
          {({ processing, errors }) => (
            <>
              {!isEdit && (
                <div className="flex flex-col gap-2">
                  <Label htmlFor="blocklist-handle">
                    {t("admin.blocklist.form.handle_label")}
                  </Label>
                  <Input
                    id="blocklist-handle"
                    name="handle"
                    type="text"
                    autoComplete="off"
                    // UX Spec §5: non-destructive dialogs autofocus the
                    // primary input, NOT Cancel. Add mode → handle Input.
                    autoFocus
                    placeholder={t("admin.blocklist.form.handle_placeholder")}
                    aria-describedby="blocklist-handle-hint"
                    aria-invalid={errors.handle ? "true" : undefined}
                    data-testid="admin-blocklist-form-handle"
                    disabled={!online}
                  />
                  <p
                    id="blocklist-handle-hint"
                    className="text-muted-foreground text-xs"
                  >
                    {t("admin.blocklist.form.handle_hint")}
                  </p>
                  <InputError messages={errors.handle} />
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Label htmlFor="blocklist-reason">
                  {t("admin.blocklist.form.reason_label")}
                </Label>
                <Textarea
                  id="blocklist-reason"
                  name="reason"
                  rows={4}
                  maxLength={280}
                  // UX Spec §5: edit mode is non-destructive (just updating
                  // the reason of an existing entry, no row removal); the
                  // reason Textarea is the only editable field, so it gets
                  // autofocus. Add mode autofocuses the handle Input above.
                  autoFocus={isEdit}
                  defaultValue={isEdit ? entry!.reason : ""}
                  placeholder={t("admin.blocklist.form.reason_placeholder")}
                  aria-describedby="blocklist-reason-hint"
                  aria-invalid={errors.reason ? "true" : undefined}
                  data-testid="admin-blocklist-form-reason"
                  disabled={!online}
                />
                <p
                  id="blocklist-reason-hint"
                  className="text-muted-foreground text-xs"
                >
                  {t("admin.blocklist.form.reason_max")}
                </p>
                <InputError messages={errors.reason} />
              </div>

              <ResponsiveDialogFooter className="gap-2 sm:gap-2">
                <Button
                  type="button"
                  variant="outline"
                  // No autoFocus on Cancel for add/edit modes per UX Spec §5
                  // — both are non-destructive. The OverwriteDialog (below)
                  // IS destructive and keeps Cancel-autoFocus.
                  onClick={() => setOpen(false)}
                >
                  {t("admin.blocklist.form.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={processing || !online}
                  aria-disabled={processing || !online ? "true" : undefined}
                  data-testid="admin-blocklist-form-submit"
                >
                  {submitLabel}
                </Button>
              </ResponsiveDialogFooter>
            </>
          )}
        </Form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

// ---------------------------------------------------------------------------
// Overwrite — server-told hot-swap from `duplicate_found` prop
// ---------------------------------------------------------------------------

interface OverwriteDialogProps {
  mode: {
    kind: "overwrite"
    duplicate: BlocklistDuplicateFound
    onClose: () => void
  }
}

function OverwriteDialog({ mode }: OverwriteDialogProps) {
  const { t, i18n } = useTranslation()
  const online = useOnline()
  const { duplicate, onClose } = mode

  const dateFmt = new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  const action = adminBlocklistEntryPath({ id: duplicate.id })
  const title = t("admin.blocklist.form.title_overwrite")

  return (
    <ResponsiveDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <ResponsiveDialogContent
        className="sm:max-w-md"
        data-testid="admin-blocklist-edit-dialog"
      >
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{title}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription className="sr-only">
            {title}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        {/*
         * OKS-120 — In mobile Sheet mode the Form is the scrollable body
         * between sticky header/footer. To keep `DuplicateWarning`
         * visible alongside the form fields (rather than stranded above
         * the scroll region), it renders as the first render-prop child
         * so it scrolls with the rest of the form body.
         */}
        <Form
          action={action}
          method="patch"
          options={{ preserveScroll: true }}
          onSuccess={onClose}
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-0"
        >
          {({ processing, errors }) => (
            <>
              <DuplicateWarning
                actorName={duplicate.blocked_by.name}
                dateLabel={dateFmt.format(new Date(duplicate.blocked_at))}
              />

              <div className="flex flex-col gap-2">
                <Label htmlFor="blocklist-handle-locked">
                  {t("admin.blocklist.form.handle_label")}
                </Label>
                <Input
                  id="blocklist-handle-locked"
                  type="text"
                  value={`@${duplicate.handle}`}
                  readOnly
                  aria-readonly="true"
                  className="bg-muted"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="blocklist-reason">
                  {t("admin.blocklist.form.reason_label")}
                </Label>
                <Textarea
                  id="blocklist-reason"
                  name="reason"
                  rows={4}
                  maxLength={280}
                  defaultValue={duplicate.reason}
                  aria-invalid={errors.reason ? "true" : undefined}
                  data-testid="admin-blocklist-form-reason"
                  disabled={!online}
                />
                <InputError messages={errors.reason} />
              </div>

              <ResponsiveDialogFooter className="gap-2 sm:gap-2">
                <Button
                  type="button"
                  variant="outline"
                  autoFocus
                  onClick={onClose}
                >
                  {t("admin.blocklist.form.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={processing || !online}
                  aria-disabled={processing || !online ? "true" : undefined}
                  data-testid="admin-blocklist-form-submit"
                >
                  {t("admin.blocklist.form.overwrite_label")}
                </Button>
              </ResponsiveDialogFooter>
            </>
          )}
        </Form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

interface DuplicateWarningProps {
  actorName: string
  dateLabel: string
}

function DuplicateWarning({ actorName, dateLabel }: DuplicateWarningProps) {
  const { t } = useTranslation()
  const raw = t("admin.blocklist.duplicate_warning_with_actor", {
    actor: `__ACTOR__${actorName}__`,
    date: dateLabel,
  })
  const parts = raw.split(/\*\*__ACTOR__|__\*\*/)
  return (
    <div
      role="alert"
      className="border-destructive/40 bg-destructive/10 text-foreground rounded-md border px-3 py-2 text-sm"
      data-testid="admin-blocklist-duplicate-warning"
    >
      {parts.length === 3 ? (
        <span>
          {parts[0]}
          <strong>{parts[1]}</strong>
          {parts[2]}
        </span>
      ) : (
        <span>{raw.replace(/\*\*/g, "")}</span>
      )}
    </div>
  )
}
